import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { trackEvent } from './TrackingPixels';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { faqs } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = ({ cms }) => {
  const d = cms || {};
  const sectionTitle = d.title || 'Book Your Service Now';
  const sectionSubtitle = d.content || 'Get in touch with us for RO installation, repair, or maintenance. Our experts are ready to help you.';
  const contactPhone = d.phone || '+91 9887068888';
  const contactEmail = d.email || 'support@rodekho.com';
  const contactWhatsapp = d.whatsapp || '+919887068888';
  const contactAddress = d.address || 'Main Tonk Road, Sector 3, Partap Nagar, Sanganer, Jaipur, Rajasthan';
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email || undefined,
          serviceType: formData.serviceType,
          message: formData.message || undefined,
        }),
      });

      if (res.ok) {
        toast.success('Booking request received! We will contact you shortly.', {
          description: 'Our team will reach out within 30 minutes.',
        });
        trackEvent.lead(formData);
        setFormData({ name: '', phone: '', email: '', serviceType: '', message: '' });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to submit booking. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please call us at +91 9887068888.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white reveal-on-scroll" data-testid="contact-section">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="contact-section-title">
            {sectionTitle.includes(' ') ? <>{sectionTitle.split(' ').slice(0, -2).join(' ')} <span className="text-blue-700">{sectionTitle.split(' ').slice(-2).join(' ')}</span></> : sectionTitle}
          </h2>
          <p className="text-lg text-gray-600" data-testid="contact-section-subtitle">
            {sectionSubtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="border-2 border-gray-100 shadow-xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg"
                  />
                </div>

                <div>
                  <label htmlFor="serviceType" className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Required *
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    required
                    value={formData.serviceType}
                    onChange={handleChange}
                    className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg bg-white"
                  >
                    <option value="">Select a service</option>
                    <option value="installation">RO Installation</option>
                    <option value="repair">RO Repair & Service</option>
                    <option value="amc">AMC Package</option>
                    <option value="filter">Filter Replacement</option>
                    <option value="purchase">Purchase New RO</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirement..."
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-500 rounded-lg resize-none"
                  />
                </div>

                <div className="flex items-start gap-3 mt-1">
                  <input
                    type="checkbox"
                    id="smsConsent"
                    required
                    className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0 cursor-pointer"
                    data-testid="sms-consent-checkbox"
                  />
                  <label htmlFor="smsConsent" className="text-xs text-gray-600 leading-relaxed cursor-pointer select-none">
                    I hereby Authorize to send notification on SMS/ messages /email / promotional messages.
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 text-white py-6 text-lg font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send size={20} className="mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info & FAQ */}
          <div className="space-y-8">
            {/* Contact Information */}
            <Card className="border-2 border-gray-100 shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <a 
                    href={`tel:${contactPhone.replace(/\s/g, '')}`}
                    className="flex items-start gap-4 p-4 rounded-lg hover:bg-blue-50 transition-colors group"
                  >
                    <div className="bg-blue-100 p-3 rounded-lg group-hover:bg-blue-200 transition-colors">
                      <Phone className="text-blue-700" size={24} />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Phone</div>
                      <div className="text-blue-700 font-medium">{contactPhone}</div>
                      <div className="text-sm text-gray-600">24/7 Support Available</div>
                    </div>
                  </a>

                  <a
                    href={`https://wa.me/${contactWhatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 rounded-lg hover:bg-green-50 transition-colors group"
                  >
                    <div className="bg-green-100 p-3 rounded-lg group-hover:bg-green-200 transition-colors">
                      <Phone className="text-green-700" size={24} />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">WhatsApp</div>
                      <div className="text-green-700 font-medium">{contactWhatsapp}</div>
                      <div className="text-sm text-gray-600">Chat with us instantly</div>
                    </div>
                  </a>

                  <a 
                    href={`mailto:${contactEmail}`}
                    className="flex items-start gap-4 p-4 rounded-lg hover:bg-blue-50 transition-colors group"
                  >
                    <div className="bg-lime-100 p-3 rounded-lg group-hover:bg-lime-200 transition-colors">
                      <Mail className="text-lime-700" size={24} />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Email</div>
                      <div className="text-blue-700 font-medium">{contactEmail}</div>
                      <div className="text-sm text-gray-600">Quick email response</div>
                    </div>
                  </a>

                  <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-blue-50 transition-colors group">
                    <div className="bg-blue-100 p-3 rounded-lg group-hover:bg-blue-200 transition-colors">
                      <MapPin className="text-blue-700" size={24} />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Location</div>
                      <div className="text-gray-700">{contactAddress}</div>
                      <div className="text-sm text-gray-600">Serving all areas in Jaipur</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <Card className="border-2 border-gray-100 shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
                <Accordion type="single" collapsible className="w-full">
                  {faqs.slice(0, 3).map((faq) => (
                    <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                      <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-700">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
